import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState, StatusBadge } from "../components/shared";
import { formatMoney, formatNum, CURRENCIES, can } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Plus, Trash2, X, MoreVertical } from "lucide-react";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2);

export default function Invoices() {
  const { user } = useAuth();
  const editable = can(user.role, "invoices");
  const base = user.organization?.base_currency || "NGN";
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { customer_id: "", warehouse_id: "", currency: base, items: [], tax_rate: 0, status: "draft", notes: "" };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    const [inv, p, c, w] = await Promise.all([
      api.get("/invoices"), api.get("/products"), api.get("/contacts?type=customer"), api.get("/warehouses"),
    ]);
    setItems(inv.data); setProducts(p.data); setCustomers(c.data); setWarehouses(w.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const prodName = (id) => products.find((p) => p.id === id)?.name || "—";
  const stockOf = (pid) => {
    const p = products.find((x) => x.id === pid);
    if (!p) return 0;
    const s = (p.stock_by_warehouse || []).find((x) => x.warehouse_id === form.warehouse_id);
    return s ? s.quantity : 0;
  };

  const openNew = () => {
    setForm({ ...empty, customer_id: customers[0]?.id || "", warehouse_id: warehouses[0]?.id || "" });
    setOpen(true);
  };
  const addItem = () => setForm((f) => { const p = products[0]; return { ...f, items: [...f.items, { product_id: p?.id || "", quantity: 1, price: p?.selling_price || 0, _key: uid() }] }; });
  const setItem = (i, k, v) => setForm((f) => { const it = [...f.items]; it[i] = { ...it[i], [k]: k === "product_id" ? v : parseFloat(v || 0) }; if (k === "product_id") { const p = products.find((x) => x.id === v); it[i].price = p?.selling_price || 0; } return { ...f, items: it }; });
  const rmItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const subtotal = form.items.reduce((s, it) => s + it.quantity * it.price, 0);
  const tax = subtotal * (form.tax_rate || 0) / 100;
  const total = subtotal + tax;

  const save = async (e) => {
    e.preventDefault();
    if (form.items.length === 0) return toast.error("Add at least one item");
    const payload = { ...form, items: form.items.map(({ product_id, quantity, price }) => ({ product_id, quantity, price })) };
    try { await api.post("/invoices", payload); toast.success("Invoice created — finished stock deducted"); setOpen(false); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => { await api.put(`/invoices/${id}/status?status=${status}`); toast.success("Status updated"); load(); };
  const del = async (id) => { if (!window.confirm("Delete invoice? Stock will be restored.")) return; await api.delete(`/invoices/${id}`); toast.success("Deleted"); load(); };

  return (
    <div data-testid="invoices-page">
      <PageHeader title="Sales Invoices" subtitle="Sell finished products — auto-deducts finished stock"
        action={editable && <Button className="rounded-none gap-2" data-testid="create-invoice-btn" onClick={openNew} disabled={products.length === 0 || customers.length === 0}><Plus className="w-4 h-4" /> New Invoice</Button>} />

      {(products.length === 0 || customers.length === 0) && editable && (
        <div className="border border-border bg-secondary/40 p-3 text-sm text-muted-foreground mb-4">Add at least one product and one customer to create invoices.</div>
      )}

      {items.length === 0 ? <EmptyState title="No invoices yet" /> : (
        <div className="border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-secondary">
              <TableHead>Number</TableHead><TableHead>Customer</TableHead><TableHead>Date</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((inv) => (
                <TableRow key={inv.id} className="hover:bg-secondary/60" data-testid="invoice-row">
                  <TableCell className="font-mono font-semibold">{inv.number}</TableCell>
                  <TableCell>{inv.customer_name}</TableCell>
                  <TableCell className="font-mono text-sm">{(inv.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell><StatusBadge status={inv.status} /></TableCell>
                  <TableCell className="text-right font-mono font-bold">{formatMoney(inv.total, inv.currency)}</TableCell>
                  <TableCell className="text-right">
                    {editable && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8 rounded-none" data-testid="invoice-menu-btn"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-none">
                          <DropdownMenuItem onClick={() => window.open(`${API}/invoices/${inv.id}/pdf`, "_blank")} data-testid="invoice-pdf-btn">Download PDF</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setStatus(inv.id, "sent")}>Mark Sent</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setStatus(inv.id, "paid")}>Mark Paid</DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive" onClick={() => del(inv.id)}>Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">New Invoice</DialogTitle><DialogDescription>Create a sales invoice; finished-product stock is deducted automatically.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Customer</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm((f) => ({ ...f, customer_id: v }))}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="invoice-customer-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Warehouse</Label>
                <Select value={form.warehouse_id} onValueChange={(v) => setForm((f) => ({ ...f, warehouse_id: v }))}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="invoice-warehouse-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Tax rate (%)</Label><Input type="number" step="any" className="rounded-none mt-1" value={form.tax_rate} onChange={(e) => setForm((f) => ({ ...f, tax_rate: parseFloat(e.target.value || 0) }))} /></div>
            </div>

            <div className="border border-border p-3 bg-secondary/40">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-xs uppercase tracking-wider">Line items</Label>
                <Button type="button" size="sm" variant="outline" className="rounded-none gap-1" onClick={addItem} data-testid="add-invoice-item-btn"><Plus className="w-3.5 h-3.5" /> Add item</Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={it._key || i} className="flex flex-wrap gap-2 items-center" data-testid="invoice-item-row">
                    <div className="flex-1 min-w-[160px]">
                      <Select value={it.product_id} onValueChange={(v) => setItem(i, "product_id", v)}>
                        <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                        <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                      <div className="text-[10px] font-mono text-muted-foreground mt-0.5">In stock: {formatNum(stockOf(it.product_id), 0)}</div>
                    </div>
                    <Input type="number" step="any" className="rounded-none w-20" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} placeholder="Qty" />
                    <Input type="number" step="any" className="rounded-none w-28" value={it.price} onChange={(e) => setItem(i, "price", e.target.value)} placeholder="Price" />
                    <Button type="button" size="icon" variant="ghost" className="h-9 w-9 rounded-none text-destructive" onClick={() => rmItem(i)}><X className="w-4 h-4" /></Button>
                  </div>
                ))}
                {form.items.length === 0 && <p className="text-xs text-muted-foreground">No items added.</p>}
              </div>
            </div>

            <div className="border border-border p-3 space-y-1 font-mono text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{formatMoney(subtotal, form.currency)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Tax ({form.tax_rate}%)</span><span>{formatMoney(tax, form.currency)}</span></div>
              <div className="flex justify-between font-bold text-base border-t border-border pt-1"><span>Total</span><span data-testid="invoice-total">{formatMoney(total, form.currency)}</span></div>
            </div>

            <DialogFooter><Button type="submit" className="rounded-none" data-testid="save-invoice-btn">Create Invoice</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
